import { differenceInHours } from "date-fns";

import { Interval } from "src/performance/types";
import { formatNumber } from "src/utils/numbers";
import { StrictDateRange } from "src/utils/timeWindow";

const INTERVALS_IN_SECONDS = {
  hour: 60 * 60,
  day: 60 * 60 * 24,
} as const;

export const getIntervalForTimeWindow = (
  timeWindow: StrictDateRange,
): Interval => {
  const windowDifference = differenceInHours(timeWindow.to, timeWindow.from);

  return windowDifference > 24 ? "day" : "hour";
};

export const intervalToSeconds = (interval: Interval) =>
  INTERVALS_IN_SECONDS[interval];

/**
 * Returns the difference between previous and current numbers.
 * The value is returned as a number that represents the percentage difference.
 *
 * @example getPercentageDiff(100, 30) // -0.7 => -70%
 * @example getPercentageDiff(100, 130) // 0.3 => 30%
 * @example getPercentageDiff(0, 100) // NaN
 */
export const getPercentageDiff = (previous: number, current: number) => {
  if (previous === 0) return NaN;
  if (current === 0) return -1;

  return ((current * 100) / previous - 100) / 100;
};

export const displayLatency = (milliseconds: number) => {
  const seconds = milliseconds / 1000;
  const minutes = seconds / 60;
  if (minutes >= 1) {
    return formatNumber(minutes, { maximumFractionDigits: 1 }) + " m";
  } else if (seconds >= 1) {
    return formatNumber(seconds, { maximumFractionDigits: 1 }) + " s";
  } else {
    return formatNumber(milliseconds, { maximumFractionDigits: 1 }) + " ms";
  }
};

/**
 * Computes the new selectedVersions value based on user selection in version picker.
 *
 * Three states:
 * - Returns undefined when all versions are selected (API receives no version filter)
 * - Returns [] when no versions are selected (queries disabled)
 * - Returns array when subset is selected (API receives specific versions)
 *
 * @param newSelection - Array of selected version IDs from the picker, or null if all deselected
 * @param allAvailableVersions - Array of all available version IDs
 * @returns undefined (all selected), [] (none selected), or array (subset selected)
 */
export const computeSelectedVersions = (
  newSelection: string[] | null,
  allAvailableVersions: string[],
): string[] | undefined => {
  if (!newSelection || newSelection.length === 0) {
    // User deselected all - disable queries
    return [];
  } else if (newSelection.length === allAvailableVersions.length) {
    // User selected all - omit versions parameter for API calls
    return undefined;
  } else {
    // User selected subset
    return newSelection;
  }
};
